import { FormControl, FormLabel, Grid, Stack } from '@chakra-ui/react'
import { InputFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import type { EditUser, LdapUser } from 'BrokerTsLib/src/dtos'
import { Product, UserType } from 'BrokerTsLib/src/enums'
import {
  useCheckUserNameExists,
  useGetUserTypes,
} from 'BrokerTsLib/src/hooks/useUser'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef, useEffect, useMemo } from 'react'
import { useDebounce } from 'use-debounce'

import { InputGroup } from '@/components'
import { FormGroup } from '@/components/FormGroup'
import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { PhoneInputGroup } from '@/containers/forms/person/PhoneInputGroup'
import { useAuth } from '@/context/AuthContext'
import { usePartner } from '@/context/PartnerContext'
import { useAvailableProducts } from '@/hooks/useProducts'

import { LoadLdapUserButton } from '../LoadLdapUserButton'

export const UserInfoFormSection = forwardRef(function UserInfoFormSection(
  props: FormSectionProps,
  ref: Ref<HTMLDivElement>,
) {
  const { user } = useAuth()
  const {
    register,
    formState: { errors, defaultValues, touchedFields },
    watch,
    setValue,
    trigger,
    control
  } = useFormContext<EditUser>()
  const { currentPartner } = usePartner()

  const userName = watch('userName')

  const insertLdapUser = (ldapUser: LdapUser) => {
    setValue('firstName', ldapUser.firstName, { shouldValidate: true })
    setValue('lastName', ldapUser.lastName, { shouldValidate: true })
    setValue('email', ldapUser.email, { shouldValidate: true })
    setValue('userNameOtherSystem', ldapUser.userNameOtherSystem, {
      shouldValidate: true,
    })
    setValue('userTypeId', UserType.ActiveDirectoryUser, {
      shouldValidate: true,
    })
  }

  const { data: userTypesData } = useGetUserTypes()

  const [userNameDebounced] = useDebounce(userName, 500)
  const { data: userNameExistsData } = useCheckUserNameExists(
    userNameDebounced,
    {
      enabled:
        userNameDebounced?.length > 0 &&
        userNameDebounced !== defaultValues?.userName,
    },
  )
  const userNameExists = useMemo(
    () =>
      userNameDebounced !== defaultValues?.userName &&
      (userNameExistsData?.data ?? false),
    [userNameDebounced, userNameExistsData, defaultValues?.userName],
  )

  useEffect(() => {
    if (touchedFields.userName)
      trigger('userName')
  }, [touchedFields, userNameExists, trigger])

  const products = useAvailableProducts()

  const vehicleInsuranceAllowed = watch('properties.vehicleInsuranceAllowed')
  const propertyInsuranceAllowed = watch('properties.propertyInsuranceAllowed')
  const travelInsuranceAllowed = watch('properties.travelInsuranceAllowed')

  return (
    <FormSection ref={ref} sectionTitle="Informace o uživateli" {...props}>
      <Stack gap={6}>
        <FormGroup>
          <FormControl flex={1} isRequired isInvalid={!!errors.userName}>
            <FormLabel htmlFor="username">Uživatelské jméno</FormLabel>
            <InputGroup
              id="userName"
              placeholder="Uživatelské jméno"
              autoComplete="username"
              errors={errors.userName}
              {...register('userName', {
                required: 'Zadejte uživatelské jméno',
                maxLength: 256,
                validate: () =>
                  userNameExists ? 'Uživatelské jméno již existuje' : undefined,
              })}
            />
          </FormControl>

          {userTypesData?.data
            .map((x) => x.id)
            .includes(UserType.ActiveDirectoryUser) && (
            <LoadLdapUserButton
              mt={9}
              flex={1}
              userName={userName}
              onLdapUserLoaded={insertLdapUser}
            />
          )}

          <FormControl flex={1} isInvalid={!!errors.userNameOtherSystem}>
            <FormLabel htmlFor="userNameOtherSystem">
              {currentPartner?.partner === 'Jap' ? 'Uživatelské jméno UCL' : 'Číslo zaměstnance'}
            </FormLabel>
            <InputGroup
              id="userNameOtherSystem"
              placeholder="Číslo zaměstnance"
              errors={errors.userNameOtherSystem}
              {...register('userNameOtherSystem', {
                maxLength: 256,
              })}
            />
          </FormControl>
        </FormGroup>

        <FormGroup>
          <FormControl flex={1} isRequired isInvalid={!!errors.firstName}>
            <FormLabel>Jméno</FormLabel>
            <InputGroup
              id="firstName"
              placeholder="Vaše jméno"
              autoComplete="given-name"
              errors={errors.firstName}
              {...register('firstName', {
                required: 'Zadejte jméno',
                maxLength: 100,
              })}
            />
          </FormControl>

          <FormControl flex={1} isRequired isInvalid={!!errors.lastName}>
            <FormLabel>Příjmení</FormLabel>
            <InputGroup
              id="lastName"
              placeholder="Vaše příjmení"
              autoComplete="given-name"
              errors={errors.lastName}
              {...register('lastName', {
                required: 'Zadejte příjmení',
                maxLength: 100,
              })}
            />
          </FormControl>
        </FormGroup>

        <FormGroup>
          <FormControl flex={2}>
            <FormLabel>Ulice</FormLabel>

            <InputGroup
              placeholder="Např. Dlouhá"
              htmlSize={24}
              {...register('addressStreet', { maxLength: 100 })}
            />
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Č.p. a č.o.</FormLabel>

            <InputGroup
              placeholder="Např. 123"
              htmlSize={8}
              {...register('addressStreetNumber', { maxLength: 10 })}
            />
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>PSČ</FormLabel>

            <InputGroup
              placeholder="Např. 110 00"
              pattern="[\d\s]+"
              htmlSize={8}
              {...register('addressPostCode', { maxLength: 20 })}
            />
          </FormControl>

          <FormControl flex={2}>
            <FormLabel>Město</FormLabel>

            <InputGroup
              placeholder="Např. Praha"
              htmlSize={24}
              {...register('addressCity', { maxLength: 100 })}
            />
          </FormControl>
        </FormGroup>

        <FormGroup>
          <FormControl flex={1} isInvalid={!!errors.phoneNumber}>
            <FormLabel>Mobilní telefon</FormLabel>

            <PhoneInputGroup
              input={register('phoneNumber', { maxLength: 256 })}
            />
          </FormControl>

          <FormControl flex={1} isRequired isInvalid={!!errors.email}>
            <FormLabel>E-mail</FormLabel>

            <InputGroup
              type="email"
              errors={errors.email}
              {...register('email', {
                required: 'Zadejte emailovou adresu',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Zadejte platnou e-mailovou adresu',
                },
                maxLength: 256,
              })}
            />
          </FormControl>
        </FormGroup>

        <SwitchFormControl name='insuringAllowed' control={control}
          label="Pojištění povoleno"
        />

        {currentPartner?.partner !== 'Kapitol' && (
          <SwitchFormControl name='properties.allowCreateInsuranceAsSomeoneElse' control={control}
            label="Sjednání za jiného uživatele"
          />
        )}

        {/* Do not show product switches for partners with single product. Only when already has value. */}
        {(products.length > 1 || (vehicleInsuranceAllowed !== undefined || travelInsuranceAllowed !== undefined || propertyInsuranceAllowed !== undefined)) &&
          <Grid
            gridTemplateColumns={{
              base: '1fr',
              sm: '1fr 1fr',
              xl: '1fr 1fr 1fr',
            }}
            gap={6}
          >
            {(products.some(p => p === Product.VehicleInsurance) || vehicleInsuranceAllowed !== undefined) &&
              <SwitchFormControl name='properties.vehicleInsuranceAllowed' control={control}
                label="Pojištění vozidel"
                defaultChecked />
            }
            {(products.some(p => p === Product.PropertyInsurance) || travelInsuranceAllowed !== undefined) &&
              <SwitchFormControl name='properties.propertyInsuranceAllowed' control={control}
                label='Pojištění majetku'
                defaultChecked />
            }
            {(products.some(p => p === Product.TravelInsurance) || propertyInsuranceAllowed !== undefined) &&
              <SwitchFormControl name='properties.travelInsuranceAllowed' control={control}
                label='Cestovní pojištění'
                defaultChecked />
            }
          </Grid>
        }

        {user?.poleas?.poleasEnabled && (
          <>
            <SwitchFormControl name='properties.importInsurance' control={control}
              label='PoLeas povolený'
            />

            <InputFormControl name='properties.formaOdmeny' control={control}
              label='Forma odměny'
              maxLength={100}
            />
          </>
        )}
      </Stack>
    </FormSection>
  )
})
